import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

//assets
import hourHand from "assets/svg/hourHand.svg";
import minuteHand from "assets/svg/minHand.svg";
import clockLogo from "assets/svg/clockLogo.svg";

// styles
import colors from "styles/colors.js";
import typographySizes from "styles/typographySizes.js";
import mediaQuery from "styles/mediaQuery.js";

const getTimePartsInZone = (timeZone) => {
  // Use Intl to avoid DST/math pitfalls
  const fmt = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone,
  });
  const parts = fmt.formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const h = parseInt(map.hour, 10);
  const m = parseInt(map.minute, 10);
  const s = parseInt(map.second, 10);
  return { h, m, s };
};

const Container = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
`;

const Face = styled.div`
  position: relative;
  border: clamp(4px, calc(15vw * 0.015), 8px) solid ${colors.dark};
  border-radius: 50%;
  width: ${({ faceClamp }) => faceClamp};
  height: ${({ faceClamp }) => faceClamp};
  box-sizing: border-box;

  @media (max-width: ${mediaQuery.laptop}) {
    width: 27vw;
    height: 27vw;
  }

`;

const hubSizePx = 12;

const Hub = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${hubSizePx}px;
  height: ${hubSizePx}px;
  background: ${colors.dark};
  border-radius: 50%;
  transform: translate(-50%, -50%);
`;

const HandImg = styled.img`
  position: absolute;
  left: 50%;
  bottom: 40%;

  transform-origin: 50% 80%;
  transform: ${({ angle, offset = 0 }) =>
    `translateX(-50%) rotate(${angle + offset}deg)`};
  transition: ${({ animate }) =>
    animate ? "transform 2.25s ease-in-out" : "none"};

  height: ${({ length }) => length};
  width: auto;
  user-select: none;
  pointer-events: none;
`;

const City = styled.p`
position: absolute  ;
top: 1.2vw;
left: 50%;
font-size: 0.75vw;
font-family: 'PPMonumentExtended', sans-serif;
transform: translate(-50%, 0%);
  color: ${colors.dark};
  margin-top: 0rem;

  @media (max-width: ${mediaQuery.laptop}) {
    top: 2vw;
    font-size: 2vw;
  }
`;

const ClockLogo = styled.img`
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translate(-50%, 0%);
  width: 6rem;

`;

/**
 * Clock
 * Props:
 * - timeZone: IANA tz (default "Europe/Warsaw")
 * - city: label under the clock (default "WWA")
 * - size: diameter in px (default 220)
 * - showSelector: show a simple timezone selector (default false)
 * - onTimeZoneChange: callback when selector changes
 */

const Clock = ({
  timeZone: timeZoneProp = "Europe/Warsaw",
  city = "WWA",
  size = 200,
  onTimeZoneChange,
}) => {
  const containerRef = useRef(null);
  const [timeZone, setTimeZone] = useState(timeZoneProp);
  // Start hands at 12:00 for the entry animation
  const [time, setTime] = useState(() => ({ h: 0, m: 0, s: 0 }));
  const [isActive, setIsActive] = useState(false);
  const [spinOffset, setSpinOffset] = useState(0);
  const mobileBreakpoint = parseInt(mediaQuery.laptop, 10);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth
  );
  const preferredWidth = useMemo(() => {
    if (!viewportWidth) return "14.5vw";
    return viewportWidth <= mobileBreakpoint ? "27vw" : "14.5vw";
  }, [mobileBreakpoint, viewportWidth]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize(); // ensure hydration picks up client width (SSR renders width 0)
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => setTimeZone(timeZoneProp), [timeZoneProp]);

  // Activate and snap to real time when the clock enters the viewport
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsActive(true);
          setSpinOffset(360);
          setTime(getTimePartsInZone(timeZone));
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [timeZone]);

  // Keep time ticking once active
  // useEffect(() => {
  //   if (!isActive) return undefined;
  //   const id = setInterval(() => {
  //     setTime(getTimePartsInZone(timeZone));
  //   }, 60000);
  //   return () => clearInterval(id);
  // }, [timeZone, isActive]);

  // Ensure immediate update if timezone changes after activation
  useEffect(() => {
    if (isActive) {
      setTime(getTimePartsInZone(timeZone));
    }
  }, [timeZone, isActive]);

  const { hourAngle, minuteAngle } = useMemo(() => {
    const h12 = time.h % 12;
    const hourAngleCalc = h12 * 30 + time.m * 0.5;
    const minuteAngleCalc = time.m * 6 + time.s * 0.1;
    return {
      hourAngle: hourAngleCalc,
      minuteAngle: minuteAngleCalc,
    };
  }, [time]);

  const faceClamp = preferredWidth;

  const handLengths = useMemo(
    () => ({
      hour: `calc(${preferredWidth} * 0.408)`,
      minute: `calc(${preferredWidth} * 0.504)`,
    }),
    [preferredWidth]
  );

  // Find matching label for selector when used

  return (
    <Container ref={containerRef}>
      <Face faceClamp={faceClamp} role="img" aria-label="Analog clock">
        <City>{city}</City>
        <ClockLogo src={clockLogo} alt="Clock Logo" />
        <HandImg
          src={hourHand}
          length={handLengths.hour}
          angle={hourAngle}
          offset={spinOffset}
          animate={isActive}
          alt=""
        />
        <HandImg
          src={minuteHand}
          length={handLengths.minute}
          angle={minuteAngle}
          offset={spinOffset}
          animate={isActive}
          alt=""
        />
        <Hub />
      </Face>
    </Container>
  );
};

export default Clock;
